import React, { useState, useEffect, useCallback } from 'react';
import styled from '@xstyled/styled-components';
import SpinnerIcon from '~/assets/icons/chatSpinner.svg';

const LOADING_MESSAGES = [
  'Thinking',
  'Give me a moment',
  'Putting thoughts in order',
  'Thinking harder',
  'Formulating',
  'Hmm',
  'Typing',
  'One second',
  'Hold on'
] as const;

const TRANSITION_DURATION = 300;
const MESSAGE_INTERVAL = 3000;

const LoadingIndicator = styled.li`
  margin: 16px 0;
`;

const LoadingIndicatorBubble = styled.div`
  background-color: #f5f5f5;
  padding: 1px 13px;
  border-radius: 18px;
  position: relative;
  display: inline-block;
  role: presentation;

  &:before,
  &:after {
    content: '';
    border-radius: 50%;
    background-color: #f5f5f5;
    position: absolute;
  }
  &:before {
    width: 16.5px;
    height: 16.5px;
    bottom: -1px;
    left: -5px;
  }

  &:after {
    width: 8.25px;
    height: 8.25px;
    bottom: -7px;
    left: -12px;
  }

  svg {
    width: 42px;
    height: 42px;
    circle {
      fill: #a9a9a9;
      stroke: #a9a9a9;
    }
  }
`;

const LoadingText = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #6f6f6f;
  padding: 6px 0;
  margin-left: -12px;
  opacity: 1;
  transition: opacity ${TRANSITION_DURATION}ms ease-in-out;

  &.fade-out {
    opacity: 0;
  }
`;

const getRandomMessageIndex = (
  currentIndex: number,
  messagesLength: number
): number => {
  if (messagesLength <= 1) return 0;

  let newIndex;
  do {
    newIndex = Math.floor(Math.random() * messagesLength);
  } while (newIndex === currentIndex);

  return newIndex;
};

export const ChatLoader: React.FC = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentMessageIndex(prevIndex =>
          getRandomMessageIndex(prevIndex, LOADING_MESSAGES.length)
        );
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }, MESSAGE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const currentMessage = LOADING_MESSAGES[currentMessageIndex];

  return (
    <LoadingIndicator aria-label="Bot is thinking" aria-live="polite">
      <LoadingIndicatorBubble aria-hidden="true">
        <SpinnerIcon />
      </LoadingIndicatorBubble>
      <LoadingText className={isTransitioning ? 'fade-out' : ''}>
        {`${currentMessage}...`}
      </LoadingText>
    </LoadingIndicator>
  );
};
